/**
 * The sound library — every clip in the project, plus the pack view a `/pack/<id>` deep
 * link lands on.
 *
 * Folders are tags. PadLab's project format has no separate folder concept, and inventing
 * one in the web build would produce files the iPhone app could not read; a tag does the
 * same job, survives the round trip, and lets one sound live in two places at once.
 */

import { h, list, on, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { chip, chipRow, hasIcon, icon, neonButton } from '../components/controls';
import { confirmDialog, emptyState, loadingBar } from '../components/sheet';
import { miniWaveform } from '../components/waveform';
import {
  LOOP_TAG,
  assetLoopEvidence,
  loopBarLength,
  loopFitRate,
  loopNeedsTempoFit,
  padIsEmpty,
  projectAsset,
  projectAssetIsLoop,
  sampleOriginDisplayName,
  type SampleAsset,
  type UUID,
} from '../../model/types';
import {
  analysis,
  assignAssetToPad,
  bufferForAsset,
  currentBankIndex,
  detectAssetTempo,
  engine,
  fitAssetToProjectTempo,
  localPacks,
  mutate,
  notify,
  notifyUndoable,
  project,
  reportFailure,
  revision,
  selectedAssetID,
  selectedPadID,
} from '../../app/state';
import { presentLoopSlicer } from '../sheets/loop-slicer';
import {
  installLibraryPack,
  installedLibraryFileNames,
  isGeneratingLibrary,
  libraryGeneration,
  startLibraryGeneration,
  stopLibraryGeneration,
  LIBRARY_PACKS,
  LIBRARY_TAG,
} from '../../app/library-install';
import { navigate } from '../../app/router';
import { bindClipboardImport, bindFileDrop, importDroppedFiles } from '../../app/import';
import { claimEditorSurface } from '../../app/transitions';
import {
  formatBytes,
  formatDuration,
  presentMenu,
  presentPrompt,
  requiresProject,
  screenBody,
  screenHeader,
  section,
} from '../../app/shell';

type Collection = 'all' | 'library' | 'favourites' | 'recent' | 'voice' | 'loops' | 'unused' | 'missing';

const COLLECTIONS: readonly { id: Collection; title: string }[] = [
  { id: 'all', title: 'All' },
  { id: 'library', title: 'Sound library' },
  { id: 'favourites', title: 'Favourites' },
  { id: 'recent', title: 'Recent' },
  { id: 'voice', title: 'Voice takes' },
  { id: 'loops', title: 'Loops' },
  { id: 'unused', title: 'Unused' },
  { id: 'missing', title: 'Needs attention' },
];

/**
 * Tags the folder rail hides.
 *
 * `root:C#3`, `key:F`, `140 bpm`, a bare note name like `c#1`, and the library's own
 * provenance tag are all written for search and for tempo matching, not for browsing. Three
 * hundred library sounds carry about ninety of them between them — twenty-five of those are
 * one per 808 semitone — and showing every one would bury the folders a person actually made
 * under a wall of machine-readable chips. They still match a search; they just do not get a
 * chip of their own.
 */
function isMachineTag(tag: string): boolean {
  return (
    tag === LIBRARY_TAG ||
    tag.startsWith('root:') ||
    tag.startsWith('key:') ||
    /^\d+ bpm$/.test(tag) ||
    /^[a-g]#?-?\d$/.test(tag)
  );
}

function usedPadIDs(): Set<UUID> {
  const open = project.peek();
  if (open === null) return new Set();
  const used = new Set<UUID>();
  for (const bank of open.banks) for (const pad of bank.pads) if (pad.assetID !== undefined) used.add(pad.assetID);
  return used;
}

/** The pad picker. Real pads, real slots, and it says which are already taken. */
function choosePad(assetID: UUID): void {
  const open = project.peek();
  if (open === null) return;
  const bank = open.banks[currentBankIndex.peek()] ?? open.banks[0];
  if (bank === undefined) return;
  presentMenu(
    `Assign to a pad in ${bank.name}`,
    [...bank.pads]
      .sort((a, b) => a.slot - b.slot)
      .map((pad) => ({
        title: pad.name === '' ? `Pad ${pad.slot + 1}` : `${pad.slot + 1}. ${pad.name}`,
        detail: padIsEmpty(pad) ? 'Empty' : 'Replaces the current sound',
        symbol: padIsEmpty(pad) ? 'square.grid.3x3.fill' : 'waveform.path',
        onSelect: () => {
          assignAssetToPad(pad.id, assetID);
          notify('Assigned', pad.name === '' ? `Pad ${pad.slot + 1}` : pad.name);
        },
      })),
  );
}

function assetMenu(asset: SampleAsset): void {
  const open = project.peek();
  const used = usedPadIDs().has(asset.id);
  const tempo = open?.tempo ?? 96;
  const evidence = open === null ? null : assetLoopEvidence(asset, tempo, open.timeSignatureBeats);
  const isLoop = evidence !== null;
  const needsFit = isLoop && loopNeedsTempoFit(asset, tempo, asset.defaultEdit.playbackRate);
  presentMenu(asset.name, [
    { title: 'Assign to a pad', symbol: 'square.grid.3x3.fill', onSelect: () => choosePad(asset.id) },
    // Slicing is the whole answer to "how do I sequence this?", so on a loop it sits second —
    // directly under assigning, above renaming and tagging. On a one-shot it is not offered
    // at all, because a one-shot is already what a step grid wants.
    ...(isLoop
      ? [
          {
            title: 'Slice into pads',
            symbol: 'rectangle.split.3x1.fill',
            detail: 'Each hit becomes a one-shot pad, with a pattern to match',
            onSelect: () => presentLoopSlicer(asset.id),
          },
        ]
      : []),
    ...(needsFit
      ? [
          {
            title: `Fit to ${Math.round(tempo)} BPM`,
            symbol: 'arrow.left.and.right',
            detail: `Plays it at ${loopFitRate(asset, tempo).toFixed(3)}× so it sits on the grid`,
            onSelect: () => fitAssetToProjectTempo(asset.id),
          },
        ]
      : []),
    // The explicit signal, offered by hand. An imported half-bar stab that is meant to be
    // held under a pattern is a loop no measurement will ever find, and the tag is what the
    // rest of the app reads. Only shown in the direction it can actually settle: a sound the
    // *tag* makes a loop can be untagged, one its own length makes a loop cannot.
    ...(evidence === null
      ? [
          {
            title: 'Treat as a loop',
            symbol: 'arrow.trianglehead.2.clockwise',
            detail: 'Loops on its pad, quantised to the bar, and plays once per pass on a track',
            onSelect: () =>
              mutate('Mark As Loop', (target) => {
                const live = target.assets.find((entry) => entry.id === asset.id);
                if (live !== undefined && !live.tags.includes(LOOP_TAG)) live.tags.push(LOOP_TAG);
              }),
          },
        ]
      : evidence === 'tag'
        ? [
            {
              title: 'Treat as a one-shot',
              symbol: 'bolt.horizontal.fill',
              detail: 'Removes the loop tag; pads assigned after this play it once',
              onSelect: () =>
                mutate('Mark As One-Shot', (target) => {
                  const live = target.assets.find((entry) => entry.id === asset.id);
                  if (live !== undefined) live.tags = live.tags.filter((tag) => tag.trim().toLowerCase() !== LOOP_TAG);
                }),
            },
          ]
        : []),
    {
      title: 'Edit sample',
      symbol: 'slider.horizontal.3',
      onSelect: () => {
        selectedAssetID.value = asset.id;
        // The row's own envelope becomes the editor's waveform.
        claimEditorSurface(
          document.querySelector<HTMLElement>(`.asset-row[data-asset-id="${asset.id}"] .library-wave`),
        );
        void navigate({ name: 'wave', projectID: open?.id, assetID: asset.id });
      },
    },
    {
      title: asset.isFavourite ? 'Remove from favourites' : 'Add to favourites',
      symbol: 'star.fill',
      onSelect: () =>
        mutate('Favourite', (target) => {
          const live = target.assets.find((entry) => entry.id === asset.id);
          if (live !== undefined) live.isFavourite = !live.isFavourite;
        }),
    },
    {
      title: 'Rename',
      symbol: 'mouth.fill',
      onSelect: () =>
        presentPrompt({
          title: 'Sound name',
          value: asset.name,
          onConfirm: (name) =>
            mutate('Rename Sound', (target) => {
              const live = target.assets.find((entry) => entry.id === asset.id);
              if (live !== undefined) live.name = name;
            }),
        }),
    },
    {
      title: 'Add to a folder',
      symbol: 'folder.fill',
      detail: asset.tags.length === 0 ? 'No folders yet' : asset.tags.join(', '),
      onSelect: () =>
        presentPrompt({
          title: 'Folder name',
          message: 'Folders are tags, so a sound can sit in several at once and the name travels with the project.',
          value: '',
          confirmTitle: 'Add',
          onConfirm: (tag) =>
            mutate('Tag Sound', (target) => {
              const live = target.assets.find((entry) => entry.id === asset.id);
              if (live !== undefined && !live.tags.includes(tag)) live.tags.push(tag);
            }),
        }),
    },
    {
      title: 'Detect tempo',
      symbol: 'clock.fill',
      detail: asset.detectedBPM === undefined ? undefined : `${Math.round(asset.detectedBPM)} BPM`,
      // One implementation, in `state.ts`, shared with the offer a loop gets when it lands on
      // a pad and with the slicer's own "find the tempo" — so all three say the same thing.
      onSelect: () => void detectAssetTempo(asset.id),
    },
    {
      title: 'Remove from project',
      symbol: 'trash.fill',
      destructive: true,
      onSelect: () => {
        confirmDialog({
          title: `Remove “${asset.name}”?`,
          message: used
            ? 'A pad is still using this sound. Removing it empties that pad. Undo brings both back.'
            : 'It is removed from this project. Undo brings it back.',
          confirmTitle: 'Remove',
          destructive: true,
          onConfirm: () => {
            mutate('Remove Sound', (target) => {
              target.assets = target.assets.filter((entry) => entry.id !== asset.id);
              for (const bank of target.banks) {
                for (const pad of bank.pads) if (pad.assetID === asset.id) delete pad.assetID;
              }
            });
            notifyUndoable('Removed', `“${asset.name}” is out of this project.`);
          },
        }).present();
      },
    },
  ]);
}

/**
 * "Loop · " when the sound is a bed, empty when it is a one-shot.
 *
 * Reads the app's one shared loop test, so a row cannot say "Loop" while the sequencer treats
 * the same sound as a one-shot, or the other way round.
 */
function assetMetaLead(asset: SampleAsset): string {
  const open = project.peek();
  if (open === null || !projectAssetIsLoop(open, asset)) return '';
  const bars = loopBarLength(asset, open.tempo, open.timeSignatureBeats, asset.defaultEdit.playbackRate);
  const rounded = bars >= 1 ? Math.round(bars * 10) / 10 : Math.round(bars * 100) / 100;
  return rounded > 0 ? `Loop · ${rounded} bar${rounded === 1 ? '' : 's'} · ` : 'Loop · ';
}

function assetRow(asset: SampleAsset): HTMLElement {
  const wave = miniWaveform({ tint: 'var(--accent-magenta)', class: 'library-wave' });
  void (async (): Promise<void> => {
    const buffer = await bufferForAsset(asset);
    if (buffer === null) return;
    try {
      // Two buckets per pixel of the row is plenty; asking for more just burns worker time.
      wave.setPeaks(await analysis.peaks(buffer, Math.max(4, Math.round(120 / Math.max(0.1, buffer.duration)))));
    } catch {
      // A row without an envelope is still a usable row.
    }
  })();

  const row = h(
    'div',
    // The id is on the row because the pending-pad handler works from the click target and
    // there is otherwise no route from a DOM node back to the asset it was built from.
    { class: 'asset-row', dataset: { assetId: asset.id } },
    h(
      'button',
      {
        class: 'asset-preview',
        type: 'button',
        'aria-label': `Preview ${asset.name}`,
        onclick: () => {
          void (async (): Promise<void> => {
            await engine.unlock();
            const buffer = await bufferForAsset(asset);
            if (buffer === null) {
              reportFailure('That sound is missing', new Error('Its file could not be found on this device.'));
              return;
            }
            if (engine.isPreviewing()) engine.stopPreview();
            else engine.previewBuffer(buffer);
          })();
        },
      },
      icon('play.fill', { size: 15 }),
    ),
    h(
      'button',
      {
        class: 'asset-main',
        type: 'button',
        'aria-label':
          asset.caption === undefined || asset.caption === ''
            ? `${asset.name}, ${formatDuration(asset.duration)}`
            : `${asset.name}. ${asset.caption}. ${formatDuration(asset.duration)}`,
        onclick: () => choosePad(asset.id),
      },
      h(
        'span',
        { class: 'asset-titles' },
        h('span', { class: 'asset-name truncate' }, asset.name),
        // Hundreds of captions are authored for the generated library and stored on the
        // asset, and nothing showed one: the row printed duration, origin and bytes, and a
        // screen-reader user heard "808 Sub Kick" with no idea what it sounds like. The
        // caption is the description the brief asks for, so it goes where a sighted reader
        // sees it and inside the row's accessible name.
        asset.caption === undefined || asset.caption === ''
          ? null
          : h('span', { class: 'asset-caption truncate' }, asset.caption),
        h(
          'span',
          { class: 'asset-meta' },
          // "Loop" leads the meta line for a bed, because it is the fact that decides what
          // happens next: it will land on its pad looping and bar-quantised, its sequencer
          // track will play once per pass, and slicing is offered on it. Said in words, in
          // the line that is already there — no badge, no second colour.
          `${assetMetaLead(asset)}${formatDuration(asset.duration)} · ${sampleOriginDisplayName(asset.origin)} · ${formatBytes(asset.byteSize)}${asset.detectedBPM === undefined ? '' : ` · ${Math.round(asset.detectedBPM)} BPM`}`,
        ),
      ),
      wave.el,
      asset.isFavourite ? icon('star.fill', { size: 14 }) : h('span'),
    ),
    h(
      'button',
      { class: 'asset-more', type: 'button', 'aria-label': `Actions for ${asset.name}`, onclick: () => assetMenu(asset) },
      icon('slider.horizontal.3', { size: 17 }),
    ),
  );
  onCleanup(row, () => wave.destroy());
  return row;
}

export function libraryScreen(context: { packID?: string } = {}): HTMLElement {
  if (project.peek() === null) return requiresProject();

  const query = signal('');
  const collection = signal<Collection>('all');
  const activeTag = signal<string | null>(null);
  const packID = context.packID;

  const libraryPack = packID === undefined ? undefined : LIBRARY_PACKS.find((entry) => entry.id === packID);

  const allTags = computed(() => {
    revision.value;
    const tags = new Set<string>();
    for (const asset of project.value?.assets ?? []) {
      for (const tag of asset.tags) if (!isMachineTag(tag)) tags.add(tag);
    }
    return [...tags].sort();
  });

  const visible = computed(() => {
    revision.value;
    const open = project.value;
    if (open === null) return [] as SampleAsset[];
    if (libraryPack !== undefined) {
      // A library pack is a list of *file names*, not of asset ids: the same sound installed
      // by two packs is one asset, and a regenerated library is a new set of ids.
      const wanted = new Set(libraryPack.entries.map((entry) => entry.fileName));
      const order = new Map(libraryPack.entries.map((entry, index) => [entry.fileName, index]));
      return open.assets
        .filter((asset) => asset.tags.includes(LIBRARY_TAG) && wanted.has(asset.originalFileName))
        .sort((a, b) => (order.get(a.originalFileName) ?? 0) - (order.get(b.originalFileName) ?? 0));
    }
    if (packID !== undefined) {
      const pack = localPacks.value.find((entry) => entry.id === packID);
      if (pack === undefined) return [] as SampleAsset[];
      return pack.assetIDs.map((id) => projectAsset(open, id)).filter((entry): entry is SampleAsset => entry !== undefined);
    }
    const used = usedPadIDs();
    const text = query.value.trim().toLowerCase();
    let assets = [...open.assets];
    switch (collection.value) {
      case 'favourites':
        assets = assets.filter((asset) => asset.isFavourite);
        break;
      case 'recent':
        assets = assets.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)).slice(0, 20);
        break;
      case 'voice':
        assets = assets.filter((asset) => asset.origin === 'microphone');
        break;
      case 'library':
        assets = assets.filter((asset) => asset.tags.includes(LIBRARY_TAG));
        break;
      case 'loops':
        // The app's one loop test, not a second definition. The old line here — "has a tempo
        // *or* is at least two seconds" — disagreed with everything else: it called a long
        // crash a loop, and at 60 BPM it called a genuine one-bar loop a one-shot.
        assets = assets.filter((asset) => projectAssetIsLoop(open, asset));
        break;
      case 'unused':
        assets = assets.filter((asset) => !used.has(asset.id));
        break;
      case 'missing':
        assets = assets.filter((asset) => asset.contentHash === '' || asset.duration <= 0);
        break;
      default:
        break;
    }
    const tag = activeTag.value;
    if (tag !== null) assets = assets.filter((asset) => asset.tags.includes(tag));
    if (text !== '') {
      assets = assets.filter(
        (asset) =>
          asset.name.toLowerCase().includes(text) ||
          asset.originalFileName.toLowerCase().includes(text) ||
          asset.tags.some((entry) => entry.toLowerCase().includes(text)),
      );
    }
    return assets;
  });

  const fileInput = h('input', {
    type: 'file',
    accept: 'audio/*,video/*',
    multiple: true,
    class: 'visually-hidden',
    onchange: (event: Event) => {
      const input = event.target as HTMLInputElement;
      const files = [...(input.files ?? [])];
      input.value = '';
      void importDroppedFiles(files, 'picker');
    },
  }) as HTMLInputElement;

  const search = h('input', {
    class: 'search-field',
    type: 'search',
    placeholder: 'Search sounds',
    'aria-label': 'Search sounds',
    autocorrect: 'off',
    spellcheck: 'false',
    oninput: (event: Event) => {
      query.value = (event.target as HTMLInputElement).value;
    },
  }) as HTMLInputElement;

  const rows = h('div', { class: 'asset-list' });
  list(rows, visible, (asset) => asset.id, assetRow, (element, asset) => {
    const name = element.querySelector('.asset-name');
    if (name !== null && name.textContent !== asset.name) name.textContent = asset.name;
  });

  const emptyHost = h('div');
  const stopEmpty = effect(() => {
    emptyHost.replaceChildren();
    if (visible.value.length > 0) return;
    emptyHost.appendChild(
      emptyState({
        symbol: 'waveform.path',
        title:
          libraryPack !== undefined
            ? `${libraryPack.name} is not installed yet`
            : packID !== undefined
              ? 'That pack is not on this device'
              : 'Nothing here yet',
        message:
          libraryPack !== undefined
            ? `${libraryPack.summary} PadLab generates every one of them on this device, so there is nothing to download and nothing to clear.`
            : packID !== undefined
              ? 'Sound pack links open packs you already have. Import a project backup that contains it, or browse the sounds in this project instead.'
              : 'Import a file, or record something in the Voice Lab. Everything you add lands here.',
        actionTitle:
          libraryPack !== undefined
            ? `Make ${libraryPack.entries.length} sounds`
            : packID !== undefined
              ? 'Browse this project'
              : 'Import a sound',
        onAction: () => {
          if (libraryPack !== undefined) installLibraryPack(libraryPack.id);
          else if (packID !== undefined) void navigate({ name: 'library', projectID: project.peek()?.id }, { replace: true });
          else fileInput.click();
        },
      }),
    );
  });
  onCleanup(emptyHost, stopEmpty);

  const tagRow = chipRow({ label: 'Folders' });
  const stopTags = effect(() => {
    const tags = allTags.value;
    tagRow.replaceChildren();
    tagRow.hidden = tags.length === 0;
    tagRow.appendChild(
      chip({
        title: 'All folders',
        // Showing every folder is where this rail starts, so it stays a plain outlined
        // chip. It used to be a second full acid pill directly under the collections
        // rail's "All" — two armed-looking controls for a user who had narrowed nothing.
        resting: true,
        selected: computed(() => activeTag.value === null),
        onPress: () => (activeTag.value = null),
      }).el,
    );
    for (const tag of tags) {
      tagRow.appendChild(
        chip({
          title: tag,
          selected: computed(() => activeTag.value === tag),
          onPress: () => (activeTag.value = activeTag.peek() === tag ? null : tag),
        }).el,
      );
    }
  });
  onCleanup(tagRow, stopTags);

  // MARK: Sound packs
  //
  // The nine packs the generated library ships as. A card says how much of its pack is on
  // this device and, while a run is going, how far along it is — the same honest count the
  // progress card above the grid shows, because a person who tapped "install" is owed the
  // number and not a spinner.

  const libraryStatus = h('div', { class: 'library-status', hidden: true });
  const statusLabel = signal('Building the sound library');
  const statusKicker = h('p', { class: 'kicker', bind: statusLabel });
  const statusMessage = h('p', { class: 'callout-message' });
  // No visible label on the bar: the kicker above it is already that sentence, and printing
  // it twice reads as a rendering bug. The bar still needs a name for a screen reader, which
  // is what the effect below keeps in step with the count.
  const statusBar = loadingBar({});
  const statusAction = neonButton({
    title: 'Pause',
    style: 'ghost',
    onPress: () => {
      if (isGeneratingLibrary()) stopLibraryGeneration();
      else startLibraryGeneration();
    },
  });
  libraryStatus.append(statusKicker, statusMessage, statusBar.el, statusAction.el);
  onCleanup(libraryStatus, () => statusBar.destroy());

  const stopStatus = effect(() => {
    const state = libraryGeneration.value;
    const idle = state.phase === 'idle' || state.phase === 'complete';
    libraryStatus.hidden = idle;
    if (idle) return;
    const fraction = state.total > 0 ? state.done / state.total : 0;
    statusBar.setProgress(state.phase === 'running' ? fraction : null);
    statusLabel.value =
      state.phase === 'running'
        ? `Building the sound library: ${state.done} of ${state.total}`
        : state.phase === 'paused'
          ? `Paused at ${state.done} of ${state.total}`
          : 'Not enough space';
    statusBar.el.setAttribute('aria-label', statusLabel.peek());
    statusMessage.textContent =
      state.message !== ''
        ? state.message
        : state.phase === 'running'
          ? `Making “${state.current}”. Every sound is generated on this device, so you can play the board while it works.`
          : 'The sounds already made are in your library.';
    statusAction.setTitle(state.phase === 'running' ? 'Pause' : 'Carry on');
    statusAction.el.hidden = state.phase === 'blocked';
  });
  onCleanup(libraryStatus, stopStatus);

  const libraryPanel = h('div', { class: 'pack-panel' });
  libraryPanel.hidden = packID !== undefined;
  for (const pack of LIBRARY_PACKS) {
    const kickerLine = h('p', { class: 'kicker' });
    const glyph = icon(hasIcon(pack.symbolName) ? pack.symbolName : 'folder.fill', { size: 22 });
    const card = h(
      'button',
      {
        class: 'pack-card',
        type: 'button',
        onclick: () => {
          const installed = installedLibraryFileNames();
          const missing = pack.entries.filter((entry) => !installed.has(entry.fileName));
          if (missing.length === 0) void navigate({ name: 'pack', packID: pack.id });
          else installLibraryPack(pack.id);
        },
      },
      // A pack card is told apart by its glyph and its kicker, never by a colour of its own.
      glyph,
      kickerLine,
      h('h3', { class: 'pack-name truncate' }, pack.name),
      h('p', { class: 'pack-summary' }, pack.summary),
    );
    const stopCard = effect(() => {
      revision.value;
      libraryGeneration.value;
      const installed = installedLibraryFileNames();
      let have = 0;
      for (const entry of pack.entries) if (installed.has(entry.fileName)) have++;
      const total = pack.entries.length;
      kickerLine.textContent =
        have === total ? `Installed · ${total} sounds` : have === 0 ? `${total} sounds` : `${have} of ${total}`;
      card.setAttribute(
        'aria-label',
        have === total ? `${pack.name}, installed, ${total} sounds. Open.` : `Install ${pack.name}, ${total} sounds.`,
      );
    });
    onCleanup(card, stopCard);
    libraryPanel.appendChild(card);
  }

  const packPanel = h('div', { class: 'pack-panel' });
  const stopPacks = effect(() => {
    const packs = localPacks.value;
    packPanel.replaceChildren();
    packPanel.hidden = packs.length === 0 || packID !== undefined;
    for (const pack of packs) {
      packPanel.appendChild(
        h(
          'button',
          {
            class: 'pack-card',
            type: 'button',
            onclick: () => void navigate({ name: 'pack', packID: pack.id }),
          },
          // A pack card is told apart by its glyph and its kicker, never by a colour of its own.
          icon('folder.fill', { size: 22 }),
          h('p', { class: 'kicker' }, `${pack.assetIDs.length} sounds`),
          h('h3', { class: 'pack-name truncate' }, pack.name),
          h('p', { class: 'pack-summary' }, pack.summary),
        ),
      );
    }
  });
  onCleanup(packPanel, stopPacks);

  // Tapping anywhere else stops a preview: an audition that keeps playing while you read
  // the next row is the fastest way to make a library feel out of control.
  const offStop = on(rows, 'scroll', () => engine.stopPreview());

  const pendingPad = selectedPadID.peek();
  const pendingPadName = ((): string => {
    const open = project.peek();
    const pad = open === null || pendingPad === null ? undefined : open.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pendingPad);
    if (pad === undefined) return 'the pad you chose';
    return pad.name === '' ? `pad ${pad.slot + 1}` : pad.name;
  })();
  const assignBanner =
    pendingPad === null
      ? null
      : h(
          'div',
          { class: 'callout callout--accent library-pending', role: 'status' },
          h('p', { class: 'kicker' }, 'Waiting for a sound'),
          h('p', { class: 'callout-message' }, `Tap any sound below to drop it onto ${pendingPadName}.`),
          neonButton({
            title: 'Cancel',
            style: 'ghost',
            onPress: () => {
              selectedPadID.value = null;
              assignBanner?.remove();
            },
          }).el,
        );

  if (pendingPad !== null) {
    /**
     * A pending pad short-circuits the picker: one tap finishes the job it started.
     *
     * This is a *capture* listener because the row's own `onclick` — which opens the ordinary
     * pad picker — is on the button itself and therefore runs before anything bubbling to
     * this container. The bubbling version this replaces called `stopPropagation` after that
     * handler had already fired, and never called `assignAssetToPad` at all: the banner
     * promised a one-tap drop and delivered a second pad picker.
     */
    const stopAssign = on(
      rows,
      'click',
      (event: Event) => {
        const main = (event.target as HTMLElement).closest('.asset-main');
        if (main === null) return;
        const assetID = (main.closest('.asset-row') as HTMLElement | null)?.dataset['assetId'];
        if (assetID === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        assignAssetToPad(pendingPad, assetID);
        // Spent. Left set, it made every later visit to this screen open in assign mode and
        // sent the next Voice Lab "save to a pad" to a pad chosen in another session.
        selectedPadID.value = null;
        assignBanner?.remove();
        const open = project.peek();
        const assigned = open === null ? undefined : projectAsset(open, assetID);
        notify('Assigned', `${assigned?.name ?? 'Sound'} → ${pendingPadName}`);
      },
      { capture: true },
    );
    onCleanup(rows, stopAssign);
  }

  const screen = h(
    'div',
    { class: 'screen screen--library' },
    screenHeader({
      kicker: packID === undefined ? 'Project sounds' : 'Sound pack',
      title: computed(() => {
        revision.value;
        if (packID === undefined) return 'Library';
        if (libraryPack !== undefined) return libraryPack.name;
        return localPacks.value.find((entry) => entry.id === packID)?.name ?? 'Sound pack';
      }),
      showsBack: packID !== undefined,
      actions: [neonButton({ title: 'Import', style: 'secondary', onPress: () => fileInput.click() }).el],
    }),
    screenBody(
      fileInput,
      assignBanner,
      // Search, collections and folders are one block of controls rather than three
      // separate rows spaced like sections; a filter bar that takes half a phone screen
      // before the first sound appears is why the library read as airy.
      packID === undefined
        ? h(
            'div',
            { class: 'filter-stack' },
            search,
            chipRow(
              { label: 'Collections' },
              ...COLLECTIONS.map(
                (entry) =>
                  chip({
                    title: entry.title,
                    // "All" is where the rail starts, not something the user armed.
                    resting: entry.id === 'all',
                    selected: computed(() => collection.value === entry.id),
                    onPress: () => (collection.value = entry.id),
                  }).el,
              ),
            ),
            tagRow,
          )
        : null,
      // The generated library comes first: on a fresh install it is the only thing on this
      // screen with anything in it, and its progress card is what explains why the sound
      // count keeps climbing.
      packID === undefined ? section('Sound packs', libraryStatus, libraryPanel) : null,
      packID === undefined ? section('Your packs', packPanel) : null,
      section(packID === undefined ? 'Sounds' : 'In this pack', rows, emptyHost),
    ),
  );
  onCleanup(screen, offStop);

  // Two more doors into the same import path. Both are listed in the brief and neither
  // existed: the implementations lived in a module nothing imported.
  onCleanup(screen, bindFileDrop(screen));
  onCleanup(screen, bindClipboardImport());
  return screen;
}
